import { QUERY_KEYS } from '@/lib';
import { fetchConversations } from '@/lib/api/conversations';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatConversation } from '@/features/chat/types';
import { isAxiosError } from 'axios';
import { debounce } from 'lodash';
import { useMemo } from 'react';

export function useConversations() {
    const queryClient = useQueryClient();
    const queryKey = QUERY_KEYS.CONVERSATIONS;
    const {
        data: conversations,
        isLoading,
        error,
        refetch,
        dataUpdatedAt, // only a timestamp AFTER first load
    } = useQuery({
        queryKey: [queryKey],
        queryFn: fetchConversations,
        staleTime: 1000 * 60 * 5,
        retry: false,
    });
    const refetchConversations = useMemo(() => debounce(refetch, 300), [refetch]);
    const isConversationListStale = !!(dataUpdatedAt && isLoading); // check after first load
    const isFirstLoad = !dataUpdatedAt && isLoading;

    const updateConversationTitle = (conversationId: string, newTitle: string) => {
        queryClient.setQueryData([queryKey], (oldData: ChatConversation[] | undefined) => {
            if (!oldData) return oldData;

            return oldData.map(conversation =>
                conversation.id === conversationId ? { ...conversation, title: newTitle } : conversation
            );
        });
    };
    const errorMessage = error
        ? isAxiosError(error)
            ? error.response?.data?.message || error?.response?.data?.error || 'Failed to fetch user data'
            : error instanceof Error
              ? error.message
              : 'Failed to fetch conversation data'
        : undefined;

    if (isAxiosError(error) && error.response?.status === 401) {
        // Let auth store handle token cleanup
        throw error;
    }

    return {
        conversations,
        isLoading,
        errorMessage,
        refetchConversations,
        isConversationListStale,
        isFirstLoad,
        updateConversationTitle,
    };
}
